import { Box } from "@mui/material";
import { SumoraMap } from "components/Parts/Atoms/SumoraMap";
import { ProgressBackdrop } from "components/Parts/Molecules/ProgressBackdrop";
import { DoshasaigaiPlugin } from "components/Parts/Organisms/SearchMap/plugins/DoshasaigaiPlugin";
import { KouzuiPlugin } from "components/Parts/Organisms/SearchMap/plugins/KouzuiPlugin";
import { MeasurePlugin } from "components/Parts/Organisms/SearchMap/plugins/MeasurePlugin";
import { PricingPlugin } from "components/Parts/Organisms/SearchMap/plugins/PricingPlugin";
import { SchoolPlugin } from "components/Parts/Organisms/SearchMap/plugins/SchoolPlugin";
import { Tile16Plugin } from "components/Parts/Organisms/SearchMap/plugins/Tile16Plugin";
import { TsunamiPlugin } from "components/Parts/Organisms/SearchMap/plugins/TsunamiPlugin";
import { YoutoPlugin } from "components/Parts/Organisms/SearchMap/plugins/YoutoPlugin";
import React, { useMemo } from "react";
import { isMobile } from "react-device-detect";
import { ClearButtons } from "../../Molecules/ClearButtons";
import {
  ContextMenuOnEstate,
  ContextMenuOnMap,
  ContextMenuOnRouteMarker,
  ContextMenuOnSchool
} from "./components";
import { SearchMapContext } from "./context";
import { useSearchMapHooks } from "./hooks";
import { SearchMapProps } from "./interfaces";
import { EstatesInfoPlugin } from "./plugins/EstatesInfoPlugin";
import { IsochronePlugin } from "./plugins/IsochronePlugin";
import { MojPlugin } from "./plugins/MojPlugin";
import { TakashioPlugin } from "./plugins/TakashioPlugin";
import { CreatingPublicEstateProgressBackdrop } from "./subComponents/CreatingPublicEstateProgressBackdrop";
import { GettingCurrentLocationProgressBackdrop } from "./subComponents/GettingCurrentLocationProgressBackdrop";
import { LimitZoomsCollapse } from "./subComponents/LimitZoomsCollapse";
import { SearchingProgressBackdrop } from "./subComponents/SearchingProgressBackdrop";

export const SearchMap = React.memo((props: SearchMapProps) => {
  const hooks = useSearchMapHooks(props);
  const additionalMapProps = useMemo(
    () => (isMobile ? { gestureHandling: "greedy" } : {}),
    []
  );
  const isStreetView = useMemo(
    () => hooks.map?.getStreetView().getVisible() ?? false,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hooks.map, hooks.triggerSwitchStreetView]
  );
  const properties = hooks.properties.current;

  return (
    <SearchMapContext.Provider
      value={{
        map: hooks.map,

        myPositionMarker: properties.myPositionMarker,
        placeMarker: properties.placeMarker,
        estateMarkers: properties.estateMarkers,
        soldEstateMarkers: properties.soldEstateMarkers,
        markerClusterer: properties.markerClusterer,

        fillingSchoolPolygons: hooks.fillingSchoolPolygons,
        searchRouteMode: props.searchRouteMode,
        measureMode: props.measureLengthMode || props.measureAreaMode,
        pricingMode: props.pricingMode,
        dspSawEstatesMode: props.dspSawEstatesMode,

        constructMarkerClustererOfEstate:
          hooks.constructMarkerClustererOfEstate,

        onReconstructMarkerClusterer: (newMarkerClusterer) =>
          (properties.markerClusterer = newMarkerClusterer),
        onMapContextMenu: hooks.onMapContextMenu,
        onSchoolContextMenu: hooks.onSchoolContextMenu,
        onFillingSchoolPolygonsChange: hooks.onFillingSchoolPolygonsChange
      }}
    >
      <Box style={props.style} className={props.className} id={props.mapDivId}>
        <SumoraMap
          center={props.center}
          zoom={props.zoom}
          streetViewControl={true}
          mapTypeControl={true}
          scaleControl={true}
          fullscreenControl={false}
          scrollwheel={true}
          mono={
            props.dspDoshasaigai ||
            props.dspKouzui ||
            props.dspTeiichitai ||
            props.dspTsunami ||
            props.dspTakashio ||
            props.dspYouto
          }
          dspPoi={props.dspPoi}
          {...additionalMapProps}
          onGoogleApiLoaded={hooks.onGoogleApiLoaded}
        />
      </Box>
      <EstatesInfoPlugin
        estates={hooks.dspEstates}
        triggerResetPosition={hooks.triggerResetPosition}
        onCloseClick={hooks.onEstateInfoCloseClick}
        onDragStart={hooks.onEstateInfoDragStart}
        onDragStop={hooks.onEstateInfoDragStop}
        onUpdateEstate={props.onUpdateEstate}
        onDeleteEstate={props.onDeleteEstate}
        onReportEstate={props.onReportEstate}
        onDisplayEstate={props.onDisplayEstate}
      />
      <DoshasaigaiPlugin on={props.dspDoshasaigai} />
      <KouzuiPlugin on={props.dspKouzui} />
      <TsunamiPlugin on={props.dspTsunami} />
      <TakashioPlugin on={props.dspTakashio} />
      <YoutoPlugin on={props.dspYouto} />
      <Tile16Plugin on={props.dspTile16} />
      <SchoolPlugin
        schoolAreas={props.schoolAreas}
        schoolPoints={props.schoolPoints}
      />
      <MojPlugin on={props.dspMoj} />
      <MeasurePlugin
        measureLengthMode={props.measureLengthMode}
        measureAreaMode={props.measureAreaMode}
      />
      <PricingPlugin
        pricingMode={props.pricingMode}
        onPricingStartClick={props.onPricingStartClick}
        onPricingEndClick={props.onQuitPricingClick}
      />
      <IsochronePlugin
        isochrones={props.isochrones}
        onRemoveIsochrone={props.onRemoveIsochrone}
      />
      <SearchingProgressBackdrop
        busy={props.busy}
        news={props.news}
        own={props.own}
        estatesCount={props.estatesCount}
        estatesTotalCount={props.estatesTotalCount}
        onModalCancelClick={props.onModalCancelClick}
      />
      <GettingCurrentLocationProgressBackdrop busyForMove={props.busyForMove} />
      <ProgressBackdrop open={props.busyForListEstateForPricing} />
      <CreatingPublicEstateProgressBackdrop
        creatingPublicEstate={props.creatingPublicEstate}
      />
      <LimitZoomsCollapse
        bound={props.boundMode}
        sold={props.soldMode}
        stickyNote={props.stickyNoteMode}
        pricing={false}
        zoom={props.zoom}
      />
      <ClearButtons
        isStreetView={isStreetView}
        measureLengthMode={props.measureLengthMode}
        measureAreaMode={props.measureAreaMode}
        pricingMode={props.pricingMode}
        fillingSchoolPolygons={hooks.fillingSchoolPolygons}
        selectingEstates={hooks.selectingEstates}
        onQuitMeasureClick={props.onQuitMeasureClick}
        onQuitPricingClick={props.onQuitPricingClick}
        onClearFillingSchoolPolygonsClick={
          hooks.onClearFillingSchoolPolygonsClick
        }
        onClearSelectedEstatesClick={hooks.onClearSelectedEstatesClick}
        onResetPositionOfEstateInfoClick={
          hooks.onResetPositionOfEstateInfoClick
        }
      />
      <ContextMenuOnEstate
        estateContextMenuPosition={hooks.estateContextMenuPosition}
        estate={hooks.estateContextMenuEstate}
        editMode={props.editMode}
        searchMode={props.searchMode}
        company={props.company}
        groups={props.groups}
        onEstateContextMenuClose={hooks.onEstateContextMenuClose}
        onSetRouteFrom={hooks.onSetRouteFrom}
        onAddRouteWaypoint={hooks.onAddRouteWaypoint}
        onSetRouteTo={hooks.onSetRouteTo}
        onUpdateEstate={hooks.onUpdateEstate}
        onDeleteEstate={hooks.onDeleteEstate}
        onIsochrone={hooks.onIsochrone}
      />
      <ContextMenuOnMap
        mapContextMenuPosition={hooks.mapContextMenuPosition}
        searchRouteMode={props.searchRouteMode}
        myPosition={props.myPosition}
        editMode={props.editMode}
        searchMode={props.searchMode}
        stickyNoteMode={props.stickyNoteMode}
        groups={props.groups}
        onMapContextMenuClose={hooks.onMapContextMenuClose}
        onSetRouteFromMyPosition={hooks.onSetRouteFromMyPosition}
        onSetRouteFrom={hooks.onSetRouteFrom}
        onAddRouteWaypoint={hooks.onAddRouteWaypoint}
        onSetRouteTo={hooks.onSetRouteTo}
        onCreateEstate={hooks.onCreateEstate}
        onCopyLinkOfGoogleMap={hooks.onCopyLinkOfGoogleMap}
        onCreateStickNote={hooks.onCreateStickNote}
        onCreateRequest={hooks.onCreateRequest}
        onIsochrone={hooks.onIsochrone}
      />
      <ContextMenuOnRouteMarker
        position={hooks.routeLocationContextMenuInfo.position}
        routeLocationStatus={
          hooks.routeLocationContextMenuInfo.routeLocationStatus
        }
        onRouteLocationContextMenuClose={hooks.onRouteLocationContextMenuClose}
        onSetRouteFrom={hooks.onSetRouteFrom}
        onSetRouteWaypoint={hooks.onSetRouteWaypoint}
        onSetRouteTo={hooks.onSetRouteTo}
        onDeleteRouteLocation={hooks.onDeleteRouteLocation}
      />
      <ContextMenuOnSchool
        schoolContextMenuPosition={hooks.schoolContextMenuPosition}
        groups={props.groups}
        onSchoolContextMenuClose={hooks.onSchoolContextMenuClose}
        onSetRouteFrom={hooks.onSetRouteFrom}
        onAddRouteWaypoint={hooks.onAddRouteWaypoint}
        onSetRouteTo={hooks.onSetRouteTo}
        onIsochrone={hooks.onIsochrone}
      />
    </SearchMapContext.Provider>
  );
});
